import useApi from '@/hooks/useApi';
import useInertiaData from '@/hooks/useInertiaData';
import React, { useEffect, useState } from 'react';
import AdminBar from '@/components/AdminBar';
import { DarkModeProvider } from '@/contexts/DarkModeContext';
import { Header, Sidebar } from './partials';
import clsx from 'clsx';

interface AppLayoutProps {
    children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const { user, no_billing_order_record, placement_required } = useInertiaData();
    const api = useApi();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const shouldBlockForNoBillingOrder = (() => {
        if (!no_billing_order_record) {
            return false;
        }
        if (window.location.pathname.startsWith('/no-billing-order')) {
            return false;
        }
        const createdAt = new Date(no_billing_order_record.created_at);
        const daysSince =
            (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysSince >= 7;
    })();

    const shouldBlockForPlacement =
        Boolean(placement_required) &&
        !window.location.pathname.startsWith('/placement-test');

    useEffect(() => {
        if (shouldBlockForNoBillingOrder) {
            window.location.href = '/no-billing-order/';
        }
    }, [shouldBlockForNoBillingOrder]);

    useEffect(() => {
        if (shouldBlockForPlacement) {
            window.location.href = '/placement-test/';
        }
    }, [shouldBlockForPlacement]);

    useEffect(() => {
        if (!user) {
            return;
        }

        const detectedTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (user.timezone !== detectedTimezone) {
            api.post('/api/update-timezone/', {
                timezone: detectedTimezone,
            }).catch((error) => {
                console.error('Failed to update timezone:', error);
            });
        }
    }, [user?.timezone, user, api]);

    return (
        <DarkModeProvider>
            <div className="app-content-bg flex min-h-screen flex-col">
                <AdminBar />
                <div className="flex flex-1">
                    <Sidebar isOpen={sidebarOpen} />

                    {/* Main Content */}
                    <div className={clsx('flex min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out', sidebarOpen && 'lg:pl-64')}>
                        <Header onToggleSidebar={() => setSidebarOpen((v) => !v)} sidebarOpen={sidebarOpen} />

                        {/* Main Content Area */}
                        <main className="flex-1 p-4 sm:p-6 lg:p-8">
                            {shouldBlockForNoBillingOrder || shouldBlockForPlacement ? null : children}
                        </main>
                    </div>
                </div>
            </div>
        </DarkModeProvider>
    );
};

export default AppLayout;
